<template>
  <div
    v-if="visible"
    class="boot-status-banner"
    :class="degraded ? 'boot-status-banner--degraded' : 'boot-status-banner--restoring'"
    role="status"
  >
    <!-- Restaurando (arranque en curso): aviso discreto, no intrusivo -->
    <template v-if="!degraded">
      <span class="boot-spinner" aria-hidden="true"></span>
      <span class="boot-message">Restaurando tu sesión…</span>
    </template>

    <!-- Degradada (restore falló por un motivo transitorio): acción explícita -->
    <template v-else>
      <span class="boot-icon" aria-hidden="true">⚠️</span>
      <div class="boot-copy">
        <strong>Sin conexión al iniciar</strong>
        <span class="boot-sub">
          Tu sesión está guardada, pero no pudimos restablecer los datos de tu empresa.
          Lo seguimos intentando automáticamente.
        </span>
      </div>
      <button
        type="button"
        class="btn boot-btn"
        :disabled="retrying"
        @click="onRetry"
      >
        {{ retrying ? 'Reconectando…' : 'Reintentar ahora' }}
      </button>
      <button
        type="button"
        class="boot-btn boot-btn--ghost"
        :disabled="retrying"
        @click="onLogout"
      >
        Cerrar sesión
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Estado del ARRANQUE visible para el usuario (c610).
 *
 * Cuando hay un token guardado pero aún no hay usuario restaurado, el guard
 * bloquea la primera navegación esperando la sesión. Antes eso era una
 * pantalla completamente en blanco (sin texto ni barra), y si el restore
 * fallaba por un motivo transitorio quedaba un blanco eterno sin ninguna
 * vía de reintento manual. Este banner — montado FUERA del <RouterView>,
 * así se ve aunque la ruta aún no haya resuelto — comunica siempre qué está
 * pasando y ofrece acción.
 *
 * En rutas públicas (/login, /signup…) no se muestra: LoginView ya tiene su
 * propio aviso de sesión almacenada (c138).
 */

const authStore = useAuthStore()
const route = useRoute()

/** El banner no debe tapar el formulario público: ahí vive otro aviso. */
const PUBLIC_PREFIXES = ['/login', '/signup', '/forgot-password', '/reset-password']

const onPublicRoute = computed(() =>
  PUBLIC_PREFIXES.some(prefix => route.path.startsWith(prefix))
)

const restoring = computed(() => !!authStore.token && !authStore.user)
const degraded = computed(() => restoring.value && authStore.sessionRestoreDegraded)
const visible = computed(() => restoring.value && !onPublicRoute.value)

const retrying = ref(false)

async function onRetry () {
  if (retrying.value) return
  retrying.value = true
  try {
    // initialize() des-duplica llamadas concurrentes vía initPromise; si una
    // restauración en segundo plano ya está en vuelo, ésta se une a ella.
    await authStore.initialize()
  } finally {
    retrying.value = false
  }
}

async function onLogout () {
  if (retrying.value) return
  // logout() limpia token/banner y redirige a /login.
  await authStore.logout()
}
</script>

<style scoped>
.boot-status-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  border-bottom-width: 1px;
  border-bottom-style: solid;
}

.boot-status-banner--restoring {
  background: #ffffff;
  border-color: #e5e7eb;
  color: #374151;
}

.boot-status-banner--degraded {
  background: #fffbeb;
  border-color: #f59e0b;
  color: #78350f;
  flex-wrap: wrap;
}

.dark .boot-status-banner--restoring {
  background: #111827;
  border-color: #1f2937;
  color: #d1d5db;
}

.dark .boot-status-banner--degraded {
  background: #451a03;
  border-color: #b45309;
  color: #fef3c7;
}

.boot-spinner {
  width: 1rem;
  height: 1rem;
  flex: none;
  border-radius: 9999px;
  border: 2px solid #cbd5e1;
  border-top-color: #4f46e5;
  animation: boot-spin 0.8s linear infinite;
}

.dark .boot-spinner {
  border-color: #374151;
  border-top-color: #818cf8;
}

@keyframes boot-spin {
  to { transform: rotate(360deg); }
}

.boot-icon {
  flex: none;
}

.boot-copy {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.boot-sub {
  font-size: 0.75rem;
  opacity: 0.85;
}

.boot-btn {
  margin-left: auto;
  flex: none;
  padding: 0.375rem 0.875rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.8125rem;
  background: #4f46e5;
  color: #ffffff;
  border: 1px solid transparent;
  cursor: pointer;
}

.boot-btn:hover:not(:disabled) {
  background: #4338ca;
}

.boot-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.boot-btn--ghost {
  margin-left: 0;
  background: transparent;
  color: inherit;
  border-color: currentColor;
}

.boot-btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

@media (max-width: 480px) {
  .boot-status-banner {
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
  }

  .boot-btn {
    margin-left: 0;
  }
}
</style>
